/**
 * Color-mode (light/dark) control with a 3-state preference.
 *
 * Chakra compiles every `_dark` semantic-token value to a `.dark &` CSS
 * selector, so switching themes is just toggling a `.dark` class on <html> —
 * no next-themes dependency.
 *
 * The user's PREFERENCE is one of 'system' | 'light' | 'dark' (persisted).
 *   - 'system' (default): follow the OS `prefers-color-scheme`, live.
 *   - 'light' / 'dark': explicit override.
 * The RESOLVED mode (what actually renders) is always 'light' | 'dark'.
 */
import { useCallback, useSyncExternalStore } from 'react';

export type ColorMode = 'light' | 'dark';
export type ColorScheme = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'bb-color-scheme';
const DARK_CLASS = 'dark';

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

/** The user's stored preference; 'system' when nothing valid is saved. */
export const getColorScheme = (): ColorScheme => {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
};

/** The mode that actually renders, after resolving 'system' against the OS. */
export const getColorMode = (): ColorMode => {
  const scheme = getColorScheme();
  if (scheme === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return scheme;
};

const applyClass = (mode: ColorMode): void => {
  document.documentElement.classList.toggle(DARK_CLASS, mode === 'dark');
};

const listeners = new Set<() => void>();
const notify = (): void => {
  for (const cb of listeners) {
    cb();
  }
};

/**
 * Apply the initial class ASAP (call once at startup, before first paint) and
 * start mirroring the OS theme. When the preference is 'system', the app
 * follows `prefers-color-scheme` live — flip your OS to dark and the UI follows.
 */
export const initColorMode = (): void => {
  applyClass(getColorMode());
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  mq?.addEventListener('change', () => {
    if (getColorScheme() !== 'system') {
      return; // explicit override wins; ignore OS changes
    }
    applyClass(getColorMode());
    notify();
  });
};

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** Set the preference (persisted) and re-apply. */
export const setColorScheme = (scheme: ColorScheme): void => {
  localStorage.setItem(STORAGE_KEY, scheme);
  applyClass(getColorMode());
  notify();
};

const NEXT_SCHEME: Record<ColorScheme, ColorScheme> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

/**
 * React hook.
 * - `colorMode`: resolved 'light' | 'dark' (for conditional rendering).
 * - `colorScheme`: the preference 'system' | 'light' | 'dark' (for the toggle icon/label).
 * - `cycleColorScheme`: system → light → dark → system.
 * - `setColorScheme`: set a specific preference.
 */
export const useColorMode = (): {
  colorMode: ColorMode;
  colorScheme: ColorScheme;
  cycleColorScheme: () => void;
  setColorScheme: (scheme: ColorScheme) => void;
} => {
  const colorMode = useSyncExternalStore(subscribe, getColorMode, () => 'light' as ColorMode);
  const colorScheme = useSyncExternalStore(
    subscribe,
    getColorScheme,
    () => 'system' as ColorScheme
  );
  const cycleColorScheme = useCallback(() => {
    setColorScheme(NEXT_SCHEME[getColorScheme()]);
  }, []);
  return { colorMode, colorScheme, cycleColorScheme, setColorScheme };
};
